import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { formatFileUrl, API_URL } from "../../utils/api";

function extractVideoId(url) {
  if (!url) return null;
  const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
}

export default function PlaylistPage() {
  const { playlistId } = useParams();
  const navigate = useNavigate();
  const [selectedVideo, setSelectedVideo] = React.useState(null);
  const [playlist, setPlaylist] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchData = async () => {
      try {
        const catRes = await fetch(`${API_URL}/library/categories?type=video`);
        if (!catRes.ok) throw new Error("API not ok");
        const categories = await catRes.json();
        const matchedCat = categories.find(c => c.link === playlistId || c._id === playlistId || c.id?.toString() === playlistId);

        if (matchedCat) {
          const itemRes = await fetch(`${API_URL}/library/items?category=${matchedCat._id}`);
          if (!itemRes.ok) throw new Error("API not ok");
          const items = await itemRes.json();
          setPlaylist({ ...matchedCat, videos: items });
        }
      } catch (err) {
        console.warn("Backend unavailable:", err);
        setPlaylist(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [playlistId]);

    if (!playlist && loading) return (
    <div className="min-h-screen bg-gray-50 pt-[80px] pb-10 px-4 sm:px-6 lg:px-8 font-libre">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 border-b border-gray-200 pb-6">
          <div className="h-4 bg-gray-200 rounded w-24 mb-4 animate-pulse"></div>
          <div className="h-10 bg-gray-200 rounded w-1/3 mb-2 animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-6">
          {Array(8).fill(0).map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
              <div className="aspect-video bg-gray-200 animate-pulse"></div>
              <div className="p-3"><div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  if (!playlist) return <div className="p-20 text-center min-h-screen italic text-gray-500">Playlist not found.</div>;

  const handleVideoClick = (video) => setSelectedVideo(video);
  const closeModal = () => setSelectedVideo(null);

  return (
    <div className="min-h-screen bg-gray-50 pt-[80px] pb-10 px-4 sm:px-6 lg:px-8 font-libre">
      {/* Background blobs preserved */}
      <div className="w-52 h-52 bg-pumpkinToast-500 fixed blur-[100px] rounded-full opacity-10 right-0 top-0 pointer-events-none"></div>
      <div className="w-52 h-52 bg-pumpkinToast-500 fixed blur-[100px] rounded-full opacity-10 bottom-0 left-0 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header Section */}
        <div className="mb-8 border-b border-gray-200 pb-6">
          <button
            onClick={() => navigate("/library")}
            className="flex items-center text-gray-600 hover:text-pumpkinToast-600 transition-colors mb-4 group"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-1 transform group-hover:-translate-x-1 transition-transform"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Library
          </button>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-pumpkinToast-600 font-AndoraModernSerif mb-2 uppercase">
                {playlist.title}
              </h1>
              <p className="text-gray-500 max-w-2xl text-sm md:text-base leading-relaxed">
                {playlist.subtitle}
              </p>
            </div>
            <div className="text-sm font-semibold text-gray-400 bg-white px-6 py-2 rounded-full shadow-sm border border-gray-100 uppercase tracking-widest">
              {(playlist.videos || []).length} Recorded Episodes
            </div>
          </div>
        </div>

        {/* Video Grid - Classic Card Design */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-6">
          {(playlist.videos || []).map((video) => {
            const videoId = extractVideoId(video.url);
            const thumbnail = video.coverImage
              ? formatFileUrl(video.coverImage)
              : (videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : "https://tipyaanacademy.com/wp-content/uploads/2023/07/muslim-school.jpg");

            return (
              <div
                key={video.id}
                onClick={() => handleVideoClick(video)}
                className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all duration-300 group cursor-pointer flex flex-col"
              >
                {/* Thumbnail Section */}
                <div className="relative aspect-video overflow-hidden bg-gray-200">
                  <img
                    src={thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors"></div>

                  {/* Play Button */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/90 text-pumpkinToast-600 rounded-full p-2 sm:p-3 shadow-lg transform scale-90 group-hover:scale-100 transition-transform">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 sm:h-6 sm:w-6"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Badge */}
                  {video.day && (
                    <span className="absolute top-2 right-2 bg-black/70 backdrop-blur-[2px] text-white text-[8px] sm:text-[10px] font-bold px-1.5 py-0.5 rounded">
                      {video.day}
                    </span>
                  )}
                </div>

                {/* Content Section */}
                <div className="px-2 py-1.5 sm:p-3 flex flex-col flex-1 relative bg-white">
                  <h3
                    className="text-gray-800 font-bold text-[10px] sm:text-sm leading-snug line-clamp-2 number-font group-hover:text-pumpkinToast-600 transition-colors mb-1"
                    title={video.title}
                  >
                    {video.title}
                  </h3>

                  <div className="mt-auto pt-1.5 sm:pt-3 flex items-center justify-between border-t border-gray-50">
                    <span className="text-[8px] sm:text-[10px] text-gray-400 font-medium uppercase tracking-wider">
                      Watch
                    </span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3 sm:h-4 sm:w-4 text-gray-300 group-hover:text-pumpkinToast-400 group-hover:translate-x-1 transition-all"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal for video playback */}
      {selectedVideo && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 sm:p-6">
          <div
            className="absolute inset-0 bg-black/90 backdrop-blur-sm transition-opacity"
            onClick={closeModal}
          ></div>

          <div className="relative w-full max-w-5xl bg-black rounded-xl overflow-hidden shadow-2xl flex flex-col z-50">
            <div className="flex items-center justify-between bg-[#1a1a1a] p-4 border-b border-gray-800">
              <h3 className="text-white text-sm sm:text-lg font-medium truncate pr-4">
                {selectedVideo.title}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="relative pt-[56.25%] bg-black">
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${extractVideoId(selectedVideo.url)}?autoplay=1&rel=0`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
